export default function animateLettersColor(
  selector = '.animate-letters-color',
  defaultStartColor = '#D5D7DA',
  defaultEndColor = '#0B1422',
  defaultMaxStagger = 1000 // null = auto; number = hard cap in ms
) {
  const cfg = { perCharDelay: 35 };

  const targets = document.querySelectorAll(selector);
  if (!targets.length) return;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const normColor = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const normMaxStagger = (v) => {
    if (v == null || v === '') return undefined; // means: use default
    if (v === 'null' || v === 'auto') return null; // means: auto
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  targets.forEach(el => {
    if (el.dataset.animated === 'true') return;

    // per-element settings (fallback to function defaults)
    const startColor = normColor(el.dataset.startColor) ?? defaultStartColor;
    const endColor = normColor(el.dataset.endColor) ?? defaultEndColor;

    const elMaxStagger = normMaxStagger(el.dataset.maxStagger);
    const maxStagger =
      elMaxStagger !== undefined ? elMaxStagger : defaultMaxStagger;

    // 1) snapshot children before rewriting
    const nodes = Array.from(el.childNodes);

    // 2) count “animatable” units first (letters + <img>)
    let totalUnits = 0;
    nodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        totalUnits += Array.from(text).filter(ch => ch !== ' ').length;
      } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'IMG') {
        totalUnits += 1;
      }
    });

    const perCharDelayMs = cfg.perCharDelay || 35;
    const autoCap = perCharDelayMs * Math.max(totalUnits - 1, 0);
    const cap = Number.isFinite(maxStagger) && maxStagger !== null ? maxStagger : autoCap;

    // 3) rebuild with spans
    el.textContent = '';
    const frag = document.createDocumentFragment();
    let charIndex = 0;

    nodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const parts = (node.textContent || '').split(/(\s+)/);
        parts.forEach(part => {
          if (part === '') return;
          if (part.trim() === '') {
            frag.appendChild(document.createTextNode(part));
          } else {
            const wordSpan = document.createElement('span');
            Array.from(part).forEach(ch => {
              const span = document.createElement('span');
              span.className = 'char';
              span.textContent = ch;
              span.style.color = startColor;

              if (!prefersReduced) {
                const delay = Math.min(charIndex * perCharDelayMs, cap);
                span.style.transitionDelay = delay + 'ms';
              }

              wordSpan.appendChild(span);
              charIndex++;
            });
            frag.appendChild(wordSpan);
          }
        });
      } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'IMG') {
        const span = document.createElement('span');
        span.className = 'char';
        if (!prefersReduced) {
          const delay = Math.min(charIndex * perCharDelayMs, cap);
          span.style.transitionDelay = delay + 'ms';
        }
        span.appendChild(node);
        frag.appendChild(span);
        charIndex++;
      } else {
        frag.appendChild(node);
      }
    });

    el.appendChild(frag);
    el.dataset.animated = 'true';

    // stash end color so the observer can read it later
    el.dataset._endColorResolved = endColor;
  });

  if (prefersReduced) {
    targets.forEach(el => el.classList.add('is-visible'));
    return;
  }

  const io = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;

      const el = entry.target;
      const endColor = normColor(el.dataset.endColor) ?? el.dataset._endColorResolved ?? defaultEndColor;

      el.classList.add('is-visible');

      // apply final color to text chars only
      el.querySelectorAll('.char').forEach(ch => {
        if (ch.firstChild && ch.firstChild.nodeType === Node.TEXT_NODE) {
          ch.style.color = endColor;
        }
      });

      obs.unobserve(el);
    });
  }, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });

  targets.forEach(el => io.observe(el));
}
